import { Collapse } from '@sgme/ui';
import classNames from 'classnames';
import { type AccordionKey, useIsAccordionOpened } from 'components/common-form/sidePanel/regulatory/context';
import { FormattedMessage } from 'components/shared/Intl';
import { getTrafficLightColor, TrafficLight } from 'components/shared/TrafficLight/TrafficLight';
import type { IntlKey } from 'locales';
import { FC, ReactNode } from 'react';
import { CustomBorderSpinner } from 'utils/customSpinner';

export interface RegulatoryComponentWrapperProps {
  title: IntlKey;
  children: ReactNode;
  componentId: AccordionKey;
  isLoading: boolean;
  isInError: boolean;
  errorMessage?: string;
}

export const RegulatoryComponentWrapper: FC<RegulatoryComponentWrapperProps> = ({
  title,
  children,
  componentId,
  isLoading: isInProgress,
  isInError,
  errorMessage,
}) => {
  const isLoading = isInProgress && !isInError && errorMessage === undefined;
  const color = getTrafficLightColor(isLoading, isInError || errorMessage !== undefined);
  const isOpened = useIsAccordionOpened(componentId);
  const shortId = componentId.replace('-accordion', '');

  return (
    <Collapse.Item itemKey={componentId} className="card bg-lvl2 mb-2">
      <Collapse.Header data-e2e={componentId} className="card-header p-3">
        <h6
          className={classNames('fw-semibold d-flex align-items-center', { 'text-secondary': !isOpened })}
          data-e2e={`toggle-${shortId}`}
        >
          <span className="me-1">
            <TrafficLight color={color} />
          </span>
          <FormattedMessage id={title} />
          {isLoading && <CustomBorderSpinner size={20} customStyle="ms-2" />}
        </h6>
      </Collapse.Header>
      <Collapse.Body>
        {errorMessage === undefined ? (
          <div className="card-body" data-e2e={componentId}>
            {children}
          </div>
        ) : (
          <div className="alert alert-discreet-danger">
            <i className="icon icon-sm pb-1 me-2">warning</i>
            <span className="fw-bold">{errorMessage}</span>
          </div>
        )}
      </Collapse.Body>
    </Collapse.Item>
  );
};
